import type { ComponentType } from 'react'
import type { AnyRoute, LinkProps } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'

import type { PermissionKey } from '@/lib/permissions'
import type { HomeWidgetDefinition } from '@/lib/home-widgets'

export interface Areas {}

export type AreaId = keyof Areas & string

type GatedArea = { [A in AreaId]: Areas[A] extends { gated: true } ? A : never }[AreaId]

export type RoutedAreaId = { [A in AreaId]: Areas[A] extends { route: AnyRoute } ? A : never }[AreaId]

export type AreaRoute<A extends RoutedAreaId> = Extract<Areas[A], { route: AnyRoute }>['route']

export type AreaDefinition = {
  id: string
  label: string
  product: string
  gated?: boolean
  route?: AnyRoute
}

export function defineArea<const A extends AreaDefinition>(area: A) {
  return area
}

export type ProductDefinition<To extends string = string> = {
  id: string
  label: string
  description: string
  icon: LucideIcon
  order: number
  routes: readonly AnyRoute[]
  entityFreeTo?: To
}

export type AreaSection = { id: string; label: string; order: number }

type EntityScopedPath = Exclude<LinkProps['to'], `${string}$${string}$${string}`>

export type NavigationItem = {
  to: EntityScopedPath
  label: string
  icon: LucideIcon
  permission?: PermissionKey
  section: string
  order: number
  activeOn?: readonly LinkProps['to'][]
  exact?: boolean
}

type RoutePath<R extends readonly AnyRoute[]> = R[number] extends infer Route
  ? Route extends { fullPath: infer P extends string }
    ? P
    : never
  : never
type WithoutTrailingSlash<P extends string> = P extends `${infer B}/` ? (B extends '' ? P : B) : P
type ModulePath<R extends readonly AnyRoute[]> = WithoutTrailingSlash<RoutePath<R>>

type ModuleNavigationItem<R extends readonly AnyRoute[], Gated extends boolean> = {
  to: Exclude<ModulePath<R>, `${string}$${string}$${string}`>
  label: string
  icon: LucideIcon
  section: string
  order: number
  activeOn?: readonly ModulePath<R>[]
  exact?: boolean
} & (Gated extends true ? { permission: PermissionKey } : { permission?: PermissionKey })

type ModuleNavigationShape<R extends readonly AnyRoute[]> = {
  [A in AreaId]?: readonly ModuleNavigationItem<R, A extends GatedArea ? true : false>[]
}

export type IntegrationCard<To extends string = string> = {
  id: string
  name: string
  description: string
  icon: LucideIcon
  order: number
  to: To
  readPermission: PermissionKey
  managePermission: PermissionKey
  connectionCount: (entityId: string) => {
    queryKey: readonly unknown[]
    queryFn: (context: { signal: AbortSignal }) => Promise<number>
  }
}

export interface ModuleContributions {
  generalSettingsSections?: readonly { id: string; component: ComponentType }[]
}

export function defineModule<const R extends readonly AnyRoute[]>(definition: {
  routes: R
  widgets?: readonly HomeWidgetDefinition[]
  products?: readonly ProductDefinition<NoInfer<ModulePath<R>>>[]
  areas?: readonly AreaDefinition[]
  sections?: { [A in AreaId]?: readonly AreaSection[] }
  navigation?: NoInfer<ModuleNavigationShape<R>>
  integrationCards?: readonly IntegrationCard<
    NoInfer<Exclude<Extract<ModulePath<R>, `/$entityId/${string}`>, `${string}$${string}$${string}`>>
  >[]
} & ModuleContributions) {
  return definition
}

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    title?: string
  }
}
